import {EventEmitter} from 'events';
import {CommMessage} from 'app/common/CommTypes';
import {Comm} from 'app/client/components/Comm';

export interface PriceUpdate {
  symbol: string;
  exchange: string;
  price: number;
  change: number;
  changePercent: number;
  timestamp: number;
  lastPrice?: number;
}

export interface FieldUpdate {
  symbol: string;
  exchange: string;
  field: string;   // e.g. 'volume', 'bid_price', 'ask_qty'
  value: number;
  timestamp: number;
  latency?: Record<string, any>;
}

// Allow listeners to receive either the original PriceUpdate or a FieldUpdate
export type OverlayUpdate = PriceUpdate | FieldUpdate;

/**
 * Thin singleton wrapper around the global Comm instance to provide
 * subscribe/unsubscribe helpers and emit overlay updates.
 */
export class LiveOverlayComm extends EventEmitter {
  private static _instance: LiveOverlayComm;
  private _comm: Comm;

  public static instance(): LiveOverlayComm {
    if (!this._instance) {
      this._instance = new LiveOverlayComm();
    }
    return this._instance;
  }

  private constructor() {
    super();
    // Attempt to obtain the global Comm instance from gristApp.
    const app = (window as any).gristApp;
    if (!app?.comm) {
      throw new Error('LiveOverlayComm: gristApp.comm not available');
    }
    this._comm = app.comm as Comm;
    // Listen for async messages.
    (this._comm as any).on('liveOverlay', (msg: CommMessage) => {
      const data = msg.data as any;
      if (Array.isArray(data)) {
        data.forEach(d => this.emit('update', d as OverlayUpdate));
      } else {
        this.emit('update', data as OverlayUpdate);
      }
    });
  }

  public subscribe(symbols: string[]) {
    if (symbols.length === 0) { return; }
    void this._comm._makeRequest(null, null, 'subscribeLiveOverlay', symbols);
  }

  public unsubscribe(symbols: string[]) {
    if (symbols.length === 0) { return; }
    void this._comm._makeRequest(null, null, 'unsubscribeLiveOverlay', symbols);
  }

  public async getLast(symbols: string[]): Promise<Record<string, PriceUpdate|null>> {
    return this._comm._makeRequest(null, null, 'getLastOverlay', symbols) as Promise<Record<string, PriceUpdate|null>>;
  }

  // Placeholder; kept for API parity with legacy websocket code.
  public sendPing() {/* no-op */}
} 